import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, User, ShoppingCart, X, ArrowRight, Instagram, Radio, Globe, ChevronLeft, ChevronRight, Minus, Plus, Trash2, Heart, LogOut, Sparkles } from "lucide-react";
import { useApp } from "./AppContext";

const API = process.env.REACT_APP_BACKEND_URL;

const AnnouncementBar = ({ announcements }) => {
  const items = announcements?.length ? [...announcements, ...announcements, ...announcements, ...announcements, ...announcements, ...announcements] : [];
  if (!items.length) return null;
  return (
    <div className="bg-[#050505] border-b border-white/[0.04] overflow-hidden py-2.5" data-testid="announcement-bar">
      <div className="ticker-animate flex whitespace-nowrap">
        {items.map((msg, i) => (
          <span key={i} className="text-[#C9A84C]/60 text-[11px] tracking-wider flex items-center font-light">
            <span className="w-1 h-1 bg-[#C9A84C]/30 rounded-full mx-6 flex-shrink-0" />
            {msg}
          </span>
        ))}
      </div>
    </div>
  );
};

const SearchOverlay = ({ isOpen, onClose }) => {
  const { t } = useApp();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/api/search?q=${encodeURIComponent(query)}`);
        if (res.ok) setResults(await res.json());
      } catch (e) { console.error("Search error:", e); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [query]);

  useEffect(() => { if (!isOpen) { setQuery(""); setResults([]); } }, [isOpen]);

  const goToProduct = (id) => { onClose(); navigate(`/product/${id}`); };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl search-overlay" onClick={onClose} data-testid="search-overlay">
      <div className="max-w-2xl mx-auto pt-24 px-6" onClick={e => e.stopPropagation()}>
        <div className="relative">
          <Search size={20} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#C9A84C]/50" />
          <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
            className="w-full bg-transparent border-b border-white/10 py-5 pl-10 pr-14 text-2xl text-white placeholder-neutral-600 focus:outline-none focus:border-[#C9A84C]/40 font-serif-brand transition-colors"
            data-testid="search-input" />
          <button onClick={onClose} className="absolute right-0 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-[#C9A84C] transition-colors" data-testid="search-close-btn">
            <X size={20} />
          </button>
        </div>
        {/* Results */}
        {query.trim() && (
          <div className="mt-8 max-h-[50vh] overflow-y-auto">
            {searching ? (
              <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-16 w-full" />)}</div>
            ) : results.length > 0 ? (
              <div className="space-y-2">
                <p className="text-neutral-500 text-[10px] uppercase tracking-[0.2em] mb-4">{results.length} {results.length > 1 ? t("searchResults_plural") : t("searchResults")}</p>
                {results.map(p => (
                  <button key={p.id} onClick={() => goToProduct(p.id)}
                    className="flex items-center gap-4 w-full p-3 hover:bg-white/[0.03] transition-all text-left group rounded-lg" data-testid={`search-result-${p.id}`}>
                    <div className="w-14 h-14 bg-[#111] overflow-hidden flex-shrink-0 border border-white/[0.06] rounded">
                      <img src={p.image} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-neutral-300 text-sm truncate group-hover:text-[#C9A84C] transition-colors">{p.name}</p>
                      <p className="text-[#C9A84C] text-xs font-semibold mt-0.5">{p.sale_price}</p>
                    </div>
                    <ArrowRight size={14} className="text-neutral-600 group-hover:text-[#C9A84C] transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-neutral-500 text-sm text-center py-8">{t("noSearchResults")} "{query}"</p>
            )}
          </div>
        )}
        {!query.trim() && (
          <div className="mt-12">
            <p className="text-neutral-500 text-[10px] uppercase tracking-[0.25em] mb-5 font-medium">Recherches populaires</p>
            <div className="flex flex-wrap gap-3">
              {["Display", "One Piece", "Booster", "Pok\u00e9mon", "Coffret", "ETB"].map(t => (
                <button key={t} onClick={() => setQuery(t)}
                  className="px-5 py-2.5 border border-white/10 text-neutral-400 text-sm hover:border-[#C9A84C]/40 hover:text-[#C9A84C] transition-all duration-300 rounded" data-testid={`search-tag-${t}`}>{t}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CartDrawer = ({ isOpen, onClose, cart, onUpdateQty, onRemove }) => {
  const { t, formatPrice } = useApp();
  const navigate = useNavigate();
  const items = cart?.items || [];
  const parsePrice = (p) => parseFloat((p || "0").replace(/[^\d,.-]/g, '').replace(',', '.')) || 0;
  const subtotal = items.reduce((sum, i) => sum + parsePrice(i.price) * i.quantity, 0);
  const freeShippingThreshold = 100;
  const progress = Math.min((subtotal / freeShippingThreshold) * 100, 100);
  const remaining = Math.max(0, freeShippingThreshold - subtotal);

  if (!isOpen) return null;
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-[55] w-[420px] max-w-[90vw] bg-white drawer-slide flex flex-col border-l border-neutral-200 shadow-2xl" data-testid="cart-drawer">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h2 className="text-[#111] font-serif-brand text-lg">{t("myCart")} <span className="text-neutral-400 text-sm font-light">({items.length})</span></h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-[#111] transition-all" data-testid="cart-close-btn">
            <X size={14} />
          </button>
        </div>
        {/* Shipping progress */}
        <div className="px-5 pt-4 pb-3 border-b border-neutral-100">
          <div className="text-xs mb-2">
            <span className="text-neutral-500 font-light">
              {progress >= 100 ? 'Livraison gratuite !' : `Plus que ${remaining.toFixed(2).replace('.', ',')}€ pour la livraison gratuite`}
            </span>
          </div>
          <div className="w-full h-1.5 bg-neutral-100 overflow-hidden rounded-full">
            <div className="shipping-bar-fill h-full" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingCart size={32} className="text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500 text-sm mb-6 font-light">{t("emptyCart")}</p>
              <button onClick={() => { onClose(); navigate('/'); }}
                className="px-8 py-3 bg-[#C9A84C] text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#b8963f] transition-all rounded-full" data-testid="continue-shopping-btn">
                Continuer mes achats
              </button>
            </div>
          ) : items.map(item => (
            <div key={item.product_id} className="flex gap-4 mb-5 pb-5 border-b border-neutral-100" data-testid={`cart-item-${item.product_id}`}>
              <Link to={`/product/${item.product_id}`} onClick={onClose}
                className="w-20 h-20 overflow-hidden flex-shrink-0 border border-neutral-200 rounded-xl hover:border-[#C9A84C]/20 transition-all">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover scale-[1.12] origin-center" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.product_id}`} onClick={onClose}>
                  <h3 className="text-neutral-700 text-sm font-medium mb-1 hover:text-[#C9A84C] transition-colors truncate">{item.name}</h3>
                </Link>
                <p className="text-[#C9A84C] font-semibold text-sm mb-3">{item.price}</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-neutral-200 rounded-full overflow-hidden">
                    <button onClick={() => onUpdateQty(item.product_id, item.quantity - 1)}
                      className="px-2.5 py-1.5 text-neutral-400 hover:text-[#C9A84C] transition-colors" data-testid={`cart-minus-${item.product_id}`}><Minus size={12} /></button>
                    <span className="text-[#111] text-sm w-8 text-center font-medium">{item.quantity}</span>
                    <button onClick={() => onUpdateQty(item.product_id, item.quantity + 1)}
                      className="px-2.5 py-1.5 text-neutral-400 hover:text-[#C9A84C] transition-colors" data-testid={`cart-plus-${item.product_id}`}><Plus size={12} /></button>
                  </div>
                  <button onClick={() => onRemove(item.product_id)}
                    className="text-neutral-400 hover:text-red-400 transition-colors" data-testid={`cart-remove-${item.product_id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="p-5 border-t border-neutral-100">
            <div className="flex justify-between mb-4">
              <span className="text-neutral-500 text-sm font-light">{t("subtotal")}</span>
              <span className="text-[#111] font-bold" data-testid="cart-subtotal">{subtotal.toFixed(2).replace('.', ',')}€</span>
            </div>
            <button onClick={() => { onClose(); navigate('/checkout'); }} className="btn-gold w-full py-3.5 bg-[#C9A84C] text-white font-bold text-xs tracking-[0.15em] uppercase hover:bg-[#b8963f] rounded-full" data-testid="checkout-btn">
              {t("checkout")}
            </button>
            <p className="text-center text-neutral-400 text-[10px] mt-3 font-light">{t("freeShippingFrom")}</p>
          </div>
        )}
      </div>
    </>
  );
};

const MenuDrawer = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    if (isOpen) fetch(`${API}/api/categories`).then(r => r.json()).then(setCategories).catch(() => {});
  }, [isOpen]);
  if (!isOpen) return null;
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed left-0 top-0 bottom-0 z-[55] w-[320px] max-w-[85vw] bg-white drawer-slide-left flex flex-col border-r border-neutral-200 shadow-2xl" data-testid="menu-drawer">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <Link to="/" onClick={onClose} className="font-serif-brand text-[#111] text-lg font-bold tracking-wide">TenTen<span className="text-[#C9A84C]">TCG</span></Link>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-[#111] transition-all" data-testid="menu-close-btn">
            <X size={14} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          <Link to="/products" onClick={onClose}
            className="flex items-center justify-between px-5 py-4 text-neutral-600 hover:text-[#C9A84C] hover:bg-[#C9A84C]/5 transition-all group" data-testid="menu-link-all">
            <span className="text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">Tous les produits</span>
            <ChevronRight size={14} className="text-neutral-300 group-hover:text-[#C9A84C]/50 transition-colors" />
          </Link>
          {categories.map((cat, i) => (
            <Link key={cat.slug} to={`/category/${cat.slug}`} onClick={onClose}
              className="flex items-center justify-between px-5 py-4 text-neutral-600 hover:text-[#C9A84C] hover:bg-[#C9A84C]/5 transition-all group" data-testid={`menu-link-${i}`}>
              <span className="text-sm font-medium group-hover:translate-x-1 transition-transform duration-300">{cat.name}</span>
              <div className="flex items-center gap-2">
                {cat.product_count > 0 && <span className="text-neutral-400 text-[10px] font-light">{cat.product_count}</span>}
                <ChevronRight size={14} className="text-neutral-300 group-hover:text-[#C9A84C]/50 transition-colors" />
              </div>
            </Link>
          ))}
        </nav>
        <div className="p-5 border-t border-neutral-100 space-y-4">
          <div className="flex gap-3">
            <a href="https://www.instagram.com/tententcg/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all"><Instagram size={15} /></a>
            <a href="https://www.whatnot.com/fr-FR/user/tententcg" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all"><Radio size={15} /></a>
          </div>
          <p className="text-neutral-400 text-[10px] font-light">2025 TenTenTCG</p>
        </div>
      </div>
    </>
  );
};

const NewsletterPopup = ({ isOpen, onClose }) => {
  const { t } = useApp();
  const [email, setEmail] = useState("");
  const [nlStatus, setNlStatus] = useState(null);
  const [nlLoading, setNlLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!email.trim()) return;
    setNlLoading(true);
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setNlStatus({ ok: res.ok, msg: data.message || data.detail });
      if (res.ok) { setEmail(""); setTimeout(onClose, 2500); }
    } catch {
      setNlStatus({ ok: false, msg: "Erreur de connexion" });
    }
    setNlLoading(false);
  };

  if (!isOpen) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 hidden md:block animate-slide-up" data-testid="newsletter-popup">
      <div className="bg-[#111]/95 backdrop-blur-lg border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-8 py-3 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Sparkles size={14} className="text-[#C9A84C] flex-shrink-0" />
            <p className="text-white/90 text-xs font-medium">{t("freeBooster")} — {t("firstVisit")}</p>
          </div>
          <div className="flex items-center gap-2">
            {nlStatus?.ok ? (
              <p className="text-emerald-400 text-xs font-medium">{nlStatus.msg}</p>
            ) : (
              <>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t("yourEmail")}
                  onKeyDown={e => e.key === 'Enter' && handleSubscribe()}
                  className="w-56 bg-white/10 border border-white/10 py-2 px-4 text-xs text-white placeholder-white/40 focus:outline-none focus:border-[#C9A84C]/50 rounded-full transition-colors" data-testid="newsletter-email-input" />
                <button onClick={handleSubscribe} disabled={nlLoading}
                  className="px-5 py-2 bg-[#C9A84C] text-black text-[10px] font-bold tracking-wider uppercase rounded-full hover:bg-[#D4B86A] transition-all disabled:opacity-50" data-testid="newsletter-submit-btn">
                  {nlLoading ? '...' : 'OK'}
                </button>
              </>
            )}
            {nlStatus && !nlStatus.ok && (
              <p className="text-red-400 text-[10px]">{nlStatus.msg}</p>
            )}
            <button onClick={onClose} className="ml-2 w-6 h-6 rounded-full flex items-center justify-center text-white/40 hover:text-white transition-colors" data-testid="newsletter-close-btn">
              <X size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = ({ cart, onOpenCart, user, onLogout }) => {
  const { lang, switchLang, t } = useApp();
  const location = useLocation();
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState([]);

  // Scroll tracking: direction + position
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem("tt_newsletter_dismissed")) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) return;
    const timer = setTimeout(() => setShowNewsletter(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 50);
        if (y > 260 && y - lastScrollY.current > 10) setHidden(true);
        else if (lastScrollY.current - y > 5 || y < 100) setHidden(false);
        lastScrollY.current = y;
        ticking.current = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    fetch(`${API}/api/site-config`).then(r => r.json()).then(d => setAnnouncements(d.announcements || [])).catch(() => {});
    fetch(`${API}/api/categories`).then(r => r.json()).then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    if (onOpenCart) setShowCart(true);
  }, [onOpenCart]);

  const totalItems = (cart?.items || []).reduce((s, i) => s + i.quantity, 0);
  const currentSlug = location.pathname.startsWith('/category/') ? location.pathname.split('/category/')[1] : '';

  return (
    <>
      {/* Announcement bar — collapses on scroll */}
      <div className={`header-collapse-bar ${scrolled ? 'collapsed' : ''}`}>
        <AnnouncementBar announcements={announcements} />
      </div>

      {/* Top utility bar — collapses on scroll */}
      <div className={`header-collapse-bar ${scrolled ? 'collapsed' : ''}`}>
        <div className="bg-[#111] border-b border-white/[0.06] py-2 px-6 md:px-12 hidden md:flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/tententcg/" target="_blank" rel="noopener noreferrer" className="header-social-icon"><Instagram size={13} /></a>
            <a href="https://www.whatnot.com/fr-FR/user/tententcg" target="_blank" rel="noopener noreferrer" className="header-social-icon"><Radio size={13} /></a>
          </div>
          <div className="flex items-center gap-3 text-neutral-500 text-xs font-light">
            <span>Une question ? Contactez-nous</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => switchLang(lang === 'fr' ? 'en' : 'fr')} className="flex items-center gap-1.5 text-neutral-500 text-xs cursor-pointer hover:text-[#C9A84C] transition-colors font-light" data-testid="lang-switcher">
              <Globe size={12} /><span>{lang === 'fr' ? 'EN' : 'FR'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main header — sticky, smart hide/reveal, glassmorphism */}
      <header
        className={`sticky top-0 z-40 border-b border-neutral-200/60 header-main ${scrolled ? 'header-scrolled' : 'bg-white'} ${hidden ? 'header-hidden' : ''}`}
        data-testid="main-header"
      >
        <div className={`flex items-center justify-between px-6 md:px-12 transition-all duration-400 ${scrolled ? 'py-2.5' : 'py-4'}`}>
          <button onClick={() => setShowMenu(true)} className="header-icon-btn" data-testid="hamburger-btn">
            <Menu size={20} />
          </button>
          <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center group" data-testid="logo">
            <span className={`font-serif-brand text-[#111] font-bold tracking-wider leading-none group-hover:text-[#C9A84C] transition-all duration-400 ${scrolled ? 'text-lg' : 'text-xl'}`}>
              TenTen<span className="text-[#C9A84C]">TCG</span>
            </span>
            <div className="w-0 h-px bg-[#C9A84C] mt-0.5 group-hover:w-full transition-all duration-500 ease-out" />
          </Link>
          <div className="flex items-center gap-5">
            <button onClick={() => setShowSearch(true)} className="header-icon-btn" data-testid="search-btn"><Search size={17} /></button>
            <Link to="/wishlist" className="header-icon-btn" data-testid="wishlist-btn"><Heart size={17} /></Link>
            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/profile" className="hidden md:flex items-center gap-1.5 text-neutral-600 text-xs font-medium hover:text-[#C9A84C] transition-colors gold-underline" data-testid="profile-link">
                  {user.name?.split(' ')[0]}
                </Link>
                <Link to="/profile" className="header-icon-btn md:hidden" data-testid="profile-btn"><User size={17} /></Link>
                <button onClick={onLogout} className="header-icon-btn hover:!text-red-500" data-testid="logout-header-btn" title="Deconnexion"><LogOut size={15} /></button>
              </div>
            ) : (
              <Link to="/auth" className="header-icon-btn" data-testid="user-btn"><User size={17} /></Link>
            )}
            <button onClick={() => setShowCart(true)} className="header-icon-btn relative" data-testid="cart-btn">
              <ShoppingCart size={17} />
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -right-2 w-4 h-4 bg-[#C9A84C] text-white text-[9px] rounded-full flex items-center justify-center font-bold cart-badge-pop">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Desktop category nav bar — stays visible, compact on scroll */}
        <nav className={`hidden md:block border-t border-neutral-100/80 transition-all duration-400 ease-out overflow-hidden ${hidden ? 'max-h-0 opacity-0' : 'max-h-14 opacity-100'}`} data-testid="category-nav">
          <div className="flex items-center justify-center gap-8 lg:gap-10 py-2.5 px-6">
            <Link
              to="/products"
              className={`header-nav-link ${!currentSlug && location.pathname === '/products' ? 'active' : ''}`}
              data-testid="nav-all-categories"
            >
              Tout voir
            </Link>
            {categories.slice(0, 5).map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`header-nav-link ${currentSlug === cat.slug ? 'active' : ''}`}
                data-testid={`nav-cat-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/blog"
              className={`header-nav-link ${location.pathname.startsWith('/blog') ? 'active' : ''}`}
              data-testid="nav-blog"
            >
              Blog
            </Link>
          </div>
        </nav>
      </header>

      <SearchOverlay isOpen={showSearch} onClose={() => setShowSearch(false)} />
      <CartDrawer isOpen={showCart} onClose={() => setShowCart(false)} cart={cart} onUpdateQty={cart?.onUpdateQty} onRemove={cart?.onRemove} />
      <MenuDrawer isOpen={showMenu} onClose={() => setShowMenu(false)} />
      <NewsletterPopup isOpen={showNewsletter} onClose={() => { setShowNewsletter(false); sessionStorage.setItem("tt_newsletter_dismissed", "1"); }} />
    </>
  );
};

export default Header;
